import { Strategy as StrategyType, VIDEO_TACTICS } from './utils'
import Styles from './StrategiesSelection.module.scss'
import { Checkbox } from 'components/CheckboxGroup/Checkbox'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { pluralize } from 'utils'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ProviderIcon } from 'screens/Calendar/WeekViewV2/CalendarEventCard/Icon'
import { BrandPlanTactic } from 'types'
import { SOCIAL_TACTICS } from './utils'
import { TACTIC_TO_PROVIDER } from 'screens/blaze/Home/VirtualMarketerHome/Brain/PlatformSection/Types'

export interface StrategyProps {
  strategy: StrategyType
  onSelect: (strategyId: string) => void
}

export const Strategy = ({ strategy, onSelect }: StrategyProps) => {
  const seoContentsCount = strategy.seo_contents.find((content) => content.type === 'blog_post')?.count || 0
  const newsletterContentsCount = strategy.email_contents.find((content) => content.type === 'newsletter')?.count || 0
  const crosspostContentsCount = strategy.crosspost_contents.find((content) => content.type === 'post')?.count || 0
  const videoCrosspostContentsCount =
    strategy.video_crosspost_contents.find((content) => content.type === 'post')?.count || 0

  const socialTactics = strategy.tactics.filter((tactic) =>
    (SOCIAL_TACTICS as readonly BrandPlanTactic[]).includes(tactic),
  )
  const videoTactics = strategy.tactics.filter((tactic) =>
    (VIDEO_TACTICS as readonly BrandPlanTactic[]).includes(tactic),
  )

  return (
    <div className={Styles.strategyContainer}>
      <Checkbox
        value={strategy.id}
        size={24}
        color="var(--selected-fill)"
        aria-label={strategy.name}
        className={Styles.checkbox}
        onChange={(isSelected) => {
          if (!isSelected) {
            onSelect(strategy.id)
          }
        }}
      >
        <div className={Styles.strategyContent}>
          <div className={Styles.strategyHeader}>
            <Heading level={3}>{strategy.name}</Heading>
            <Text variant="secondary" color="var(--dark-60)">
              {strategy.description}
            </Text>
          </div>
          <div className={Styles.strategyValues}>
            {crosspostContentsCount > 0 && (
              <>
                <div className={Styles.strategyValue}>
                  <div className={Styles.providerIcons}>
                    {socialTactics.map((provider) => (
                      <ProviderIcon
                        key={provider}
                        provider={
                          TACTIC_TO_PROVIDER.find((t) => t.tactic === provider)?.provider as ExternalIntegrationEnum
                        }
                        size={16}
                        isHovered={false}
                        zIndex={0}
                        borderColor="rgba(247, 247, 247, 1)"
                        marginLeft={-5}
                      />
                    ))}
                  </div>
                  <Text variant="secondary">
                    {crosspostContentsCount} {pluralize('post', crosspostContentsCount)}
                  </Text>
                  <Text variant="secondary" color="var(--dark-40)">
                    /week
                  </Text>
                </div>
              </>
            )}
            {videoCrosspostContentsCount > 0 && (
              <>
                <div className={Styles.separator} />
                <div className={Styles.strategyValue}>
                  <div className={Styles.providerIcons}>
                    {videoTactics.map((provider) => (
                      <ProviderIcon
                        key={provider}
                        provider={
                          TACTIC_TO_PROVIDER.find((t) => t.tactic === provider)?.provider as ExternalIntegrationEnum
                        }
                        size={16}
                        isHovered={false}
                        zIndex={0}
                        isReels
                        borderColor="rgba(247, 247, 247, 1)"
                      />
                    ))}
                  </div>
                  <Text variant="secondary">
                    {videoCrosspostContentsCount} {pluralize('video', videoCrosspostContentsCount)}
                  </Text>

                  <Text variant="secondary" color="var(--dark-40)">
                    /week
                  </Text>
                </div>
              </>
            )}
            {newsletterContentsCount > 0 && (
              <>
                <div className={Styles.separator} />
                <div className={Styles.strategyValue}>
                  <Text variant="secondary">
                    {newsletterContentsCount} {pluralize('email', newsletterContentsCount)}
                  </Text>
                  <Text variant="secondary" color="var(--dark-40)">
                    /week
                  </Text>
                </div>
              </>
            )}
            {seoContentsCount > 0 && (
              <>
                <div className={Styles.separator} />
                <div className={Styles.strategyValue}>
                  <Text variant="secondary">
                    {seoContentsCount} {pluralize('blog', seoContentsCount)}
                  </Text>
                  <Text variant="secondary" color="var(--dark-40)">
                    /week
                  </Text>
                </div>
              </>
            )}
          </div>
        </div>
      </Checkbox>
    </div>
  )
}
